import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	type RequestPasswordResetFormData,
	requestPasswordResetSchema,
} from "@/features/auth/schemas/password-reset";
import { auth } from "@/lib/auth";

export const Route = createFileRoute("/_auth/_auth-layout/forgot-password")({
	component: ForgotPassword,
});

function ForgotPassword() {
	const emailId = useId();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [sent, setSent] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<RequestPasswordResetFormData>({
		resolver: zodResolver(requestPasswordResetSchema),
		defaultValues: {
			email: "",
		},
	});

	async function onSubmit(data: RequestPasswordResetFormData) {
		setError(null);
		setIsSubmitting(true);

		try {
			const result = await auth.requestPasswordReset({
				email: data.email,
				redirectTo: `${window.location.origin}/reset-password`,
			});

			if (result.error) {
				throw new Error(result.error.message || "Falha ao solicitar redefinição");
			}

			setSent(true);
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: "Não foi possível solicitar a redefinição.",
			);
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col items-center gap-2 text-center">
				<h1 className="text-xl font-bold">Redefinir senha</h1>
				<FieldDescription>
					Informe seu e-mail para receber o link de redefinição.
				</FieldDescription>
			</div>

			{sent ? (
				<FieldGroup>
					<Field>
						<div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
							Se o e-mail existir, enviaremos o link de redefinição.
						</div>
					</Field>
					<FieldDescription className="text-center">
						<Link to="/signin" className="underline">
							Voltar para o login
						</Link>
					</FieldDescription>
				</FieldGroup>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<FieldGroup>
						<Field>
							<FieldLabel htmlFor={emailId}>Email</FieldLabel>
							<Input
								id={emailId}
								type="email"
								placeholder="m@example.com"
								{...register("email")}
							/>
							<FieldError errors={errors.email ? [errors.email] : []} />
						</Field>
						{error && (
							<Field>
								<div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
									{error}
								</div>
							</Field>
						)}
						<Field>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? "Enviando..." : "Enviar link"}
							</Button>
						</Field>
						<FieldDescription className="text-center">
							<Link to="/signin" className="underline">
								Voltar para o login
							</Link>
						</FieldDescription>
					</FieldGroup>
				</form>
			)}
		</div>
	);
}
